import { useEffect, useMemo, useState } from "react";
import { SearchIcon } from "lucide-react";

import { ResponsiveLine } from "@nivo/line";
import { GRAPH_ANIMATION_CONFIG } from "@config/global";
import { GRAPHS_EXTENDED_COLOR_PALETTE } from "@config/color";
import { Combobox } from "@ui/ComboBox";
import { cn } from "@ui/shadCN/lib/utils";
import { hashStringToRange } from "@utils/format";
import { GetObservationInfo } from "@hooks/useReport/GetIndicatorInfo";

import { useObservationsCTX } from "pages/monitoring/hooks/useObservationsCTX";
import { ConfidenceIntervalLayer } from "pages/monitoring/outlets/initiatives/observations/card/utils/ConfidenceIntervalLayer";
import type { LineData } from "pages/monitoring/types/observations";
import { uiText } from "pages/monitoring/outlets/initiatives/observations/layout/uiText";
import { GraphLegend } from "@ui/GraphLegend";
import { getSeriesColor } from "pages/monitoring/outlets/initiatives/observations/card/utils/colors";

const customColorMap: Record<string, string> = {
  "Probabilidad de detección": GRAPHS_EXTENDED_COLOR_PALETTE[2],
  "Probabilidad de ocupación": GRAPHS_EXTENDED_COLOR_PALETTE[22],
};

export function DetectionProbabilityWithoutCovariables() {
  const { currentObservation } = useObservationsCTX();
  const [selectedSpecie, setSelectedSpecie] = useState<string>("");

  const speciesOptions = useMemo(
    () =>
      (currentObservation?.groups ?? []).map((group) => {
        const commonName = group.category?.description
          ? `${group.category?.description}, `
          : "";
        return {
          value: group.category.name,
          label: `${commonName}${group.category.name}`,
        };
      }),
    [currentObservation?.groups],
  );

  const { renderIndicatorInfo, keys } = useMemo(() => {
    if (!currentObservation) {
      return { renderIndicatorInfo: [], keys: [] };
    }

    const renderIndicatorInfo: (LineData & { id: string })[] = [];
    const keys: string[] = [];

    ((currentObservation.cleanData ?? []) as LineData[]).forEach((serie) => {
      const key = serie.metricName || serie.id;
      renderIndicatorInfo.push({ ...serie, id: key });
      keys.push(key);
    });

    return { renderIndicatorInfo, keys };
  }, [currentObservation]);

  const filteredData = useMemo(
    () =>
      renderIndicatorInfo.filter(
        (serie) => serie.scientificName === selectedSpecie,
      ),
    [renderIndicatorInfo, selectedSpecie],
  );

  const selectedSpecieTitle = useMemo(() => {
    const current = currentObservation?.groups.find(
      (specie) => specie.category.name === selectedSpecie,
    );

    return current
      ? {
          name: current.category.name,
          commonName: current.category.description,
        }
      : { name: undefined, commonName: undefined };
  }, [currentObservation?.groups, selectedSpecie]);

  useEffect(() => {
    if (!currentObservation) {
      return;
    }

    setSelectedSpecie(speciesOptions[0].value ?? "");
  }, [currentObservation, speciesOptions]);

  return !currentObservation ? null : (
    <>
      <div className="p-4 shrink-0 space-y-4 border border-muted mb-0 rounded-lg hover:border-primary/50 transition-colors duration-300">
        {speciesOptions.length > 1 && (
          <Combobox
            items={speciesOptions ?? []}
            value={selectedSpecie}
            setValue={setSelectedSpecie}
            uiText={uiText.observationCard.detectionProbabilityWCov.selector}
            icon={SearchIcon}
            keys={{ forLabel: "label", forValue: "value" }}
          />
        )}

        <h4
          className={cn(
            speciesOptions.length > 1
              ? "border-t border-muted m-0! mt-4 pt-4"
              : "m-0!",
            "text-primary",
          )}
        >
          <div>
            {selectedSpecieTitle?.commonName ?? selectedSpecieTitle.name}
          </div>
          {selectedSpecieTitle?.commonName && (
            <div className="text-base italic font-light">
              {selectedSpecieTitle.name}
            </div>
          )}
        </h4>
      </div>

      <GetObservationInfo
        graphId={
          selectedSpecieTitle.commonName
            ? `${selectedSpecieTitle.commonName}, ${selectedSpecieTitle.name}`
            : (selectedSpecieTitle.name ?? "")
        }
        mapUrl={null}
        mapElementId={null}
      >
        <>
          <div className="w-full h-full aspect-video">
            <ResponsiveLine
              data={filteredData}
              margin={{ top: 20, right: 30, bottom: 30, left: 30 }}
              motionConfig={GRAPH_ANIMATION_CONFIG}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: 0,
                max: 1,
              }}
              colors={(serie) =>
                customColorMap[serie.id] ??
                getSeriesColor(
                  hashStringToRange(
                    String(serie.id),
                    GRAPHS_EXTENDED_COLOR_PALETTE.length,
                  ),
                  GRAPHS_EXTENDED_COLOR_PALETTE,
                )
              }
              pointSize={10}
              useMesh={true}
              layers={[
                "grid",
                "axes",
                ConfidenceIntervalLayer,
                "crosshair",
                "lines",
                "points",
                "mesh",
                "legends",
              ]}
              tooltip={({ point }) => {
                const [name] = point.seriesId
                  .replace(/\|\|.*$/, "")
                  .split(", ");

                const data = point.data.y;
                const date = point.data.x;

                return (
                  <div
                    className="bg-background px-4 py-2 shadow-md rounded flex flex-col items-center"
                    style={{ pointerEvents: "none", whiteSpace: "nowrap" }}
                  >
                    <div className="flex flex-col text-center text-sm mb-1 *:m-0!">
                      <span className="font-normal">
                        <span
                          className="inline-block w-3 h-3 mr-1 rounded-full"
                          style={{ backgroundColor: point.seriesColor }}
                        />
                        {name}
                      </span>
                      <span className="italic">{date}</span>
                    </div>

                    <table className="space-x-1 [&_td]:px-2 [&_tr_td]:first:text-right">
                      <tbody>
                        <tr>
                          <td>
                            {
                              uiText.observationCard.rangedTooltip
                                .upperLimitTitle
                            }
                          </td>
                          <td>{point.data?.upperLimit ?? data}</td>
                        </tr>
                        <tr>
                          <td>Índice</td>
                          <td>{data}</td>
                        </tr>
                        <tr>
                          <td>
                            {
                              uiText.observationCard.rangedTooltip
                                .lowerLimitTitle
                            }
                          </td>
                          <td>{point.data?.lowerLimit ?? data}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              }}
            />
          </div>

          <GraphLegend
            keys={keys}
            customColorMap={customColorMap}
            isBar={false}
          />
        </>
      </GetObservationInfo>
    </>
  );
}
